// supportStaffGate.js
//
// Centralised client for the support-staff pick + OTP envelope used by the
// admin fallback gate inside `validateSecurityPermissionNoPrompt` (see
// `src/store/modules/user_module.js`) and the existing admin login flow at
// `src/components/login/steps/{UserSelectionStep,OtpVerificationStep}.vue`.
//
// Why this exists:
// - The admin login flow today hardcodes the AES secret `'your-secret-key'`
//   and the axios endpoints inline. The new admin fallback gate needs the
//   same envelope, so this module centralises both so future security tickets
//   only have to touch one file.
// - The fallback gate is a session-level concern (D5): once a support staff
//   is on record via `localStorage.supportAgent`, the gate short-circuits and
//   does NOT prompt again. Only when no support staff is on record does this
//   service actually go to the network.
//
// Out of scope for this slice:
// - Hardening the `'your-secret-key'` constant (move to env / config) —
//   follow-up security ticket.

import axios from 'axios';
import CryptoJS from 'crypto-js';
import { Storage } from '@/services/Storage';

// AES secret shared with the existing UserSelectionStep / OtpVerificationStep
// login flow. Localised here so the gate and the login steps share one
// implementation. Future hardening: read from a config / env var.
const SUPPORT_STAFF_PAYLOAD_SECRET = 'your-secret-key';

// localStorage keys — same names the login flow uses so we are fully
// interoperable with anything that has already populated them.
const LS_USERS_KEY = 'users';
const LS_SUPPORT_AGENT_KEY = 'supportAgent';

/**
 * Fetch the list of active support staff from the ticket API.
 *
 * Mirrors the call in `UserSelectionStep.vue:75` —
 * `axios.get(`${ticketApi}support/getActiveSupportStaff`)`.
 *
 * @returns {Promise<Array<{id: string|number, name?: string, username?: string, email?: string}>>}
 *          Active support staff list. Each entry is shaped like the response
 *          from the ticket API (no client-side massaging).
 * @throws {Error} When the network request fails. Callers should surface this
 *                  via the `responses.showWarning` helper.
 */
async function fetchSupportStaff() {
    const response = await axios.get(`${ticketApi}support/getActiveSupportStaff`);
    return response.data;
}

/**
 * Request an OTP for the chosen support staff. Mirrors the call in
 * `UserSelectionStep.vue:104` plus the CryptoJS envelope at lines 96-101.
 *
 * Side effects:
 * - Persists `{ userID, storeDB }` to `localStorage.users` (AES-encrypted with
 *   `SUPPORT_STAFF_PAYLOAD_SECRET`) so the OTP step can re-send if needed.
 * - Triggers a server-side OTP send to the chosen support staff.
 *
 * @param {string|number} userID - The chosen support staff id.
 * @param {string} storeDB - The current store database name.
 * @returns {Promise<void>}
 */
async function requestOtp(userID, storeDB) {
    const payload = { userID, storeDB };

    // Encrypt payload before storing — same envelope as UserSelectionStep.
    const encryptedPayload = CryptoJS.AES.encrypt(
        JSON.stringify(payload),
        SUPPORT_STAFF_PAYLOAD_SECRET
    ).toString();
    localStorage.setItem(LS_USERS_KEY, encryptedPayload);

    await axios.post(`${ticketApi}support/requestOTP`, payload);
}

/**
 * Re-send an OTP for the previously-picked support staff. Mirrors
 * `OtpVerificationStep.vue:115-137` — reads `localStorage.users`, decrypts
 * it, then re-POSTs to the requestOTP endpoint.
 *
 * Used by the OTP step's "Resend OTP" button.
 *
 * @returns {Promise<void>}
 * @throws {Error} When no payload is in storage or the network call fails.
 */
async function resendOtp() {
    const encryptedPayload = localStorage.getItem(LS_USERS_KEY);
    if (!encryptedPayload) {
        throw new Error('No user data found in storage.');
    }
    const bytes = CryptoJS.AES.decrypt(encryptedPayload, SUPPORT_STAFF_PAYLOAD_SECRET);
    const payload = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    await axios.post(`${ticketApi}support/requestOTP`, payload);
}

/**
 * Verify the OTP entered by the operator. Mirrors `OtpVerificationStep.vue:96`.
 *
 * On success the response carries `{ username }` — the verified support
 * staff username. The caller is responsible for:
 * - Dispatching `recordlogin` (audit trail) — see `user_module.js`.
 * - Setting `localStorage.supportAgent` so the next gate invocation can
 *   short-circuit (D5).
 * - Clearing `localStorage.users` (one-time envelope).
 *
 * @param {string} otp - The 5-digit OTP entered by the operator.
 * @returns {Promise<{username: string}>} The verified support staff username.
 * @throws {Error} When verification fails. The error may carry a
 *                 `response.data.message` from the server.
 */
async function verifyOtp(otp) {
    const response = await axios.post(`${ticketApi}support/verifyOTP`, { otp });
    return { username: response.data.username };
}

/**
 * Read the currently-recorded support staff, if any. The fallback gate uses
 * this to short-circuit when a support staff was already established by the
 * admin login flow.
 *
 * @returns {string|null} The support staff username, or `null` if none on
 *                        record for this session.
 */
function getRecordedSupportAgent() {
    return localStorage.getItem(LS_SUPPORT_AGENT_KEY);
}

/**
 * Record a verified support staff on the current session. Called by the
 * fallback gate after a successful OTP verification.
 *
 * @param {string} username - The verified support staff username.
 */
function recordSupportAgent(username) {
    localStorage.setItem(LS_SUPPORT_AGENT_KEY, username);
    // Clear the one-time encrypted payload now that verification has
    // succeeded — same housekeeping `OtpVerificationStep.vue:101` does.
    localStorage.removeItem(LS_USERS_KEY);
}

/**
 * Clear the recorded support staff. Called on logout so we don't attribute
 * later actions (none should exist, but defence in depth) to a staff no
 * longer on shift.
 */
function clearRecordedSupportAgent() {
    localStorage.removeItem(LS_SUPPORT_AGENT_KEY);
}

export const supportStaffGate = {
    fetchSupportStaff,
    requestOtp,
    resendOtp,
    verifyOtp,
    getRecordedSupportAgent,
    recordSupportAgent,
    clearRecordedSupportAgent,
};

export default supportStaffGate;